// Copyright (c) Docugami, Inc. All rights reserved.

import { ADD_PIPELINE_BUILD, PipelineBuildState, REMOVE_PIPELINE_BUILD } from "store/organization/types";
import { UPDATE_ClUSTERS_STATE, SET_DOC_FETCH_STATE, UPDATE_CLUSTER_INFO, REMOVE_FROM_CLUSTER, ADD_TO_CLUSTER, ADD_NEW_CLUSTER, REMOVE_CLUSTERS, ADJUST_CLUSTER_DOC_COUNT } from "./types";
import { ClustersState } from "./types";

const initialState: ClustersState = {
    isFetching: false,
    fetched: false,
    indexed: {},
    allIds: []
};

export function clustersReducer(
    state = initialState,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    action: any
): ClustersState
{
    switch (action.type)
    {
        case UPDATE_ClUSTERS_STATE:
        {
            return {
                ...state,
                ...action.payload
            };
        }

        case SET_DOC_FETCH_STATE:
        {
            return {
                ...state,
                indexed: {
                    ...state.indexed,
                    [action.payload.clusterId]: {
                        ...state.indexed[action.payload.clusterId],
                        documentsFetchState: action.payload.fetchState
                    }
                }
            }
        }

        case UPDATE_CLUSTER_INFO:
        {
            const cluster = state.indexed[action.payload.clusterId];
            if (!cluster) {
                return state;
            }
        
            return {
                ...state,
                indexed: {
                    ...state.indexed,
                    [action.payload.clusterId]: {
                        ...state.indexed[action.payload.clusterId],
                        clusterInfo: {...action.payload}
                    }
                }
            }
        }

        case ADD_TO_CLUSTER:
        {
            let cluster = state.indexed[action.payload.clusterId];
            if (!cluster) {
                return state;
            }

            let docIds: string[] = action.payload.folderIds;

            if (cluster.documentsFetchState.fetched) {
                let filtered = cluster.docIds.filter(x => !action.payload.folderIds.includes(x));
                docIds = [...filtered, ...action.payload.folderIds];
            }

            return {
                ...state,
                indexed: {
                    ...state.indexed,
                    [action.payload.clusterId]: {
                        ...cluster,
                        docIds: docIds,
                        clusterInfo: {
                            ...cluster.clusterInfo,
                            numberOfDocuments: docIds.length
                        }
                    }
                }
            }
        }

        case REMOVE_FROM_CLUSTER:
        {
            let cluster = state.indexed[action.payload.clusterId];
            if (!cluster) {
                return state;
            }

            let docIds: string[] = cluster.docIds;

            if (cluster.documentsFetchState.fetched) {
                docIds = cluster.docIds.filter(x => !action.payload.folderIds.includes(x));
            }

            return {
                ...state,
                indexed: {
                    ...state.indexed,
                    [action.payload.clusterId]: {
                        ...cluster,
                        docIds: docIds,
                        clusterInfo: {
                            ...cluster.clusterInfo,
                            numberOfDocuments: docIds.length
                        }
                    }
                }
            }
        }

        case ADD_NEW_CLUSTER:
        {
            let clusterState = {
                documentsFetchState: { fetching: false, fetched: true },
                clusterInfo: action.payload,
                docIds: []
            }

            return {
                ...state,
                allIds: [action.payload.clusterId, ...state.allIds],
                indexed: {
                    ...state.indexed,
                    [action.payload.clusterId]: clusterState
                }
            }
        }

        case REMOVE_CLUSTERS:
        {
            let allIds = state.allIds.filter(x => action.payload.findIndex((y: string) => y === x) === -1);
            let indexed = {...state.indexed};
            for (let id of action.payload) {
                delete indexed[id];
            }

            return {
                ...state,
                allIds: allIds,
                indexed: indexed
            }
        }

        case ADJUST_CLUSTER_DOC_COUNT:
        {
            const cluster = state.indexed[action.payload.clusterId];
            if (!cluster) {
                return state;
            }

            const currentCount = cluster.clusterInfo.numberOfDocuments ?? 0;
            const newCount = Math.max(0, currentCount + action.payload.delta);

            return {
                ...state,
                indexed: {
                    ...state.indexed,
                    [action.payload.clusterId]: {
                        ...cluster,
                        clusterInfo: {
                            ...cluster.clusterInfo,
                            numberOfDocuments: newCount
                        }
                    }
                }
            }
        }

        case ADD_PIPELINE_BUILD:
        case REMOVE_PIPELINE_BUILD: {
            const build: PipelineBuildState = action.payload;
            const clusterId: string = build.buildRequest.clusterId;
            const cluster = state.indexed[clusterId ?? ''];
            if (!cluster) {
                return state;
            }

            const buildStatus = cluster.clusterInfo.buildStatus ?? {};

            const buildState = build.state;
            const buildType = build.buildRequest.buildType;

            return {
                ...state,
                indexed: {
                    ...state.indexed,
                    [clusterId]: {
                        ...cluster,
                        clusterInfo: {
                            ...cluster.clusterInfo,
                            buildStatus: {
                                ...buildStatus,
                                [buildType]: buildState
                            }
                        }
                    }
                }
            }
        }

        default:
            return state;
    }
}
